import React, { useEffect, useRef } from "react";
import {
  Navigate,
  useLocation,
  useOutlet,
} from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./components/LandingPage";
import AdminPanel from "./components/AdminPanel";
import HomeGate from "./components/site/HomeGate";
import EmployersHome from "./components/site/EmployersHome";
import CandidatesHome from "./components/site/CandidatesHome";
import SectorPage from "./components/site/SectorPage";
/* Dedicated (spoke) pages */
import CandidateJourneyPage from "./components/site/CandidateJourneyPage";
import CandidateSectorsPage from "./components/site/CandidateSectorsPage";
import ExternalRedirect from "./components/site/ExternalRedirect";
import EmployerHowItWorksPage from "./components/site/EmployerHowItWorksPage";
import EmployerWhyPage from "./components/site/EmployerWhyPage";
import EmployerSectorsPage from "./components/site/EmployerSectorsPage";
import BriefPage from "./components/site/BriefPage";
import TrustPage from "./components/site/TrustPage";
import FAQPage from "./components/site/FAQPage";
import GuidesHub from "./components/site/GuidesHub";
import GuidePage from "./components/site/GuidePage";
import NotFound from "./components/site/NotFound";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import PageSEO from "./components/PageSEO";
import SmoothScroll from "./components/SmoothScroll";
import ScrollProgress from "./components/ScrollProgress";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.28, ease: [0.65, 0, 0.35, 1] } },
};

/* Wraps the routed page in an enter transition. `useOutlet()` yields the matched child
   route element. Keying the motion.div by pathname unmounts the previous page immediately
   (no stale/stacked pages) and fades the new one in. We deliberately avoid AnimatePresence:
   under SSG its hydration handling could orphan pre-rendered nodes -> duplicated content. */
function AnimatedOutlet() {
  const location = useLocation();
  const outlet = useOutlet();
  /* Skip the enter-fade on the very first paint so the initial route's content is
     visible immediately (framer starting at opacity:0 delayed LCP — and, under SSG,
     would bake opacity:0 into the pre-rendered HTML). Route changes after mount still
     animate. */
  const firstRender = useRef(true);
  const initial = firstRender.current ? false : "initial";
  useEffect(() => {
    firstRender.current = false;
  }, []);

  return (
    <motion.div
      key={location.pathname}
      initial={initial}
      animate="enter"
      variants={pageVariants}
    >
      {outlet}
    </motion.div>
  );
}

/* Site shell (chrome). Rendered once as the layout route; the active page renders into
   <AnimatedOutlet/>. Everything here previously lived in <App/> between <Router> and the
   route table. */
function Layout() {
  return (
    <>
      <PageSEO />
      <ScrollToTop />
      <AnalyticsTracker />
      <ScrollProgress />
      <SmoothScroll>
        <div className="App">
          <AnimatedOutlet />
          <Toaster position="top-right" />
        </div>
      </SmoothScroll>
    </>
  );
}

/* Route table as react-router data-router records — the shape vite-react-ssg needs to
   pre-render real HTML per URL. The set matches the previous <Routes> exactly. */
export const routes = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <HomeGate /> },

      /* Candidate journey — short landing + dedicated pages */
      { path: "candidates", element: <CandidatesHome /> },
      { path: "candidates/journey", element: <CandidateJourneyPage /> },
      { path: "candidates/sectors", element: <CandidateSectorsPage /> },
      /* Jobs + registration now live on the ERP portal — redirect (also 301'd server-side). */
      { path: "candidates/jobs", element: <ExternalRedirect to="https://jobs.adelgroups.com/" /> },
      { path: "candidates/apply", element: <ExternalRedirect to="https://jobs.adelgroups.com/apply" /> },
      /* Gulf country guides — gated (noindex/unlinked/not in sitemap) until GUIDES_PUBLISHED */
      { path: "candidates/guides", element: <GuidesHub /> },
      { path: "candidates/guides/:slug", element: <GuidePage /> },

      /* Employer journey — short landing + dedicated pages */
      { path: "employers", element: <EmployersHome /> },
      { path: "employers/how-it-works", element: <EmployerHowItWorksPage /> },
      { path: "employers/why-adel", element: <EmployerWhyPage /> },
      { path: "employers/sectors", element: <EmployerSectorsPage /> },
      { path: "employers/brief", element: <BriefPage /> },

      { path: "sectors/:slug", element: <SectorPage /> },

      /* Trust & licensing + FAQ */
      { path: "trust", element: <TrustPage /> },
      { path: "faq", element: <FAQPage /> },

      /* Legacy pages retained (301'd to v2 at hosting time) */
      { path: "about", element: <LandingPage page="about" /> },
      { path: "services", element: <LandingPage page="services" /> },
      { path: "industries", element: <LandingPage page="industries" /> },
      { path: "industries/:slug", element: <LandingPage page="industry-detail" /> },
      { path: "jobs", element: <LandingPage page="jobs" /> },
      { path: "job-seekers", element: <LandingPage page="job-seekers" /> },
      { path: "contact", element: <LandingPage page="contact" /> },
      { path: "admin", element: <AdminPanel /> },

      /* Legacy /preview/* URLs redirect to the live gate */
      { path: "preview/*", element: <Navigate to="/" replace /> },
      { path: "preview", element: <Navigate to="/" replace /> },

      /* Catch-all 404 — PageSEO marks unmatched routes noindex */
      { path: "*", element: <NotFound /> },
    ],
  },
];

export default routes;
